import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  AlertComponent,
  BreadcrumbComponent,
  Button,
} from "../../../common/components";
import LexicalEditor from "../../../common/components/text-editor/text-editor";
import "./user-terms-of-services.scss";
import icons from "../../../assets/icons/icons";
import {
  createTermsOfServices,
  editTermsOfServices,
  getTermsOfServices,
} from "../../../api/terms-of-services";
import { useLogin } from "../../../common/hooks/useUser";
import { formatLastUpdatedDate } from "../user-management-role/user-management-role-utils";
import { useSetLoading } from "../../../common/hooks/appearance";

const UserTermsOfService: React.FC = () => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showAFPTos, setShowAFPTos] = useState<boolean>(false);
  const [editedTermsOfServices, setEditedTermsOfServices] = React.useState<{
    termsOfServiceJson: any;
    termsOfServiceHtml: any;
  }>({ termsOfServiceJson: null, termsOfServiceHtml: null });
  const [termsOfServices, setTermsOfServices] = React.useState<any>(null);
  const [alert, setAlert] = React.useState<{
    isOpen?: boolean;
    message?: any;
    type?: string;
  }>({});

  const loginData: any = useLogin();
  const isSuperTenant = loginData?.tenantDetails?.tenantType === "SuperTenant";
  const isHavingTermsOfServices = isSuperTenant
    ? termsOfServices?.defaultTermsOfServices
    : termsOfServices?.tenantTermsOfServices;

  const crumbMapping = [
    {
      label: "User Management",
      link: "#",
      handleClick: () => {},
    },
    { label: "Update Terms Of Service", link: "#" },
  ];

  const handleCancel = () => {
    setIsEdit(false);
    setShowAFPTos(false);
    setEditedTermsOfServices({
      termsOfServiceJson: null,
      termsOfServiceHtml: null,
    });
  };

  const handleNewTermsOfService = async (data: any) => {
    try {
      setIsLoading(true);
      const res = await createTermsOfServices(data);
      fetchTermsOfServices();
      setIsEdit(false);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching terms of service:", err);
      setAlert({
        isOpen: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          err?.error ||
          "Error fetching terms of service.",
        type: "error",
      });
      setIsLoading(false);
    }
  };

  const handleEditTermsOfService = async (id: any, data: any) => {
    try {
      setIsLoading(true);
      const res = await editTermsOfServices(id, data);
      fetchTermsOfServices();
      setIsEdit(false);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching terms of service:", err);
      setAlert({
        isOpen: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          err?.error ||
          "Error fetching terms of service.",
        type: "error",
      });
      setIsLoading(false);
    }
  };

  const handleSave = async (html: any, children: any) => {
    const { defaultTermsOfServices, tenantTermsOfServices } =
      termsOfServices || {};

    const id = isSuperTenant
      ? defaultTermsOfServices?.id
      : tenantTermsOfServices?.id;

    const data = {
      termsOfServiceJson: children,
      termsOfServiceHtml: html,
    };

    if (html === '<p class="editor-paragraph"><br></p>') {
      setAlert({
        isOpen: true,
        message: "Add your Terms of Service so users know what to expect.",
        type: "error",
      });
      return;
    }

    if (id) {
      handleEditTermsOfService(id, data);
    } else {
      handleNewTermsOfService(data);
    }
  };

  const fetchTermsOfServices = async () => {
    try {
      setIsLoading(true);
      const response = await getTermsOfServices();

      setTermsOfServices({
        defaultTermsOfServices: response?.data?.data?.defaultTermsOfServices,
        tenantTermsOfServices: response?.data?.data?.tenantTermsOfServices,
      });
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching terms of service:", err);
      setIsLoading(false);

      setAlert({
        isOpen: true,
        message:
          err?.response?.data?.message ||
          err?.message ||
          err?.error ||
          "Error fetching terms of service.",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchTermsOfServices();
  }, []);

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleUseAFP = () => {
    setShowAFPTos(true);
    setIsEdit(true);
    setIsDialogOpen(false);
  };

  const handleContinueCustom = () => {
    setIsEdit(true);
    setIsDialogOpen(false);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const termsDialog = (
    open: any,
    onClose: any,
    onUseAFP: any,
    onContinueCustom: any
  ) => {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>
          <strong>Terms Of Services</strong>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Do you want to use <strong>AFP's Terms of Service</strong> in the
            editor, or continue with your own?
          </Alert>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Button label="Cancel" className="tos-cancel-btn" onClick={onClose} />
          <Box>
            <Button label="Use AFP Terms" onClick={onUseAFP} />{" "}
            <Button label="Continue with My Own" onClick={onContinueCustom} />
          </Box>
        </DialogActions>
      </Dialog>
    );
  };

  const termsHtml =
    editedTermsOfServices?.termsOfServiceHtml ??
    isHavingTermsOfServices?.terms_of_service_html;

  const isEmptyHtml = termsHtml === '<p class="editor-paragraph"><br></p>';
  const shouldShowBanner =
    !isEdit && (isEmptyHtml || (!isHavingTermsOfServices && !isLoading));

  return (
    <div className="terms-of-service user-terms-of-services">
      <Box sx={{ marginBottom: "30px" }}>
        <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      </Box>

      {!isEdit && (
        <Box className="edit-button-wrapper">
          <p>
            Last Update :{" "}
            {isHavingTermsOfServices
              ? formatLastUpdatedDate(isHavingTermsOfServices?.updated_at)
              : ""}{" "}
          </p>
          <Button
            label={isHavingTermsOfServices ? "Edit" : "Add Terms Of Services"}
            onClick={() => {
              if (!isHavingTermsOfServices) {
                setIsDialogOpen(true);
                return;
              }
              setIsEdit((prev: boolean) => !prev);
            }}
          />
        </Box>
      )}

      {shouldShowBanner && !isLoading && (
        <Box className="alert-terms-of-service">
          <img src={icons.TermOfServicesInfo} alt="img" />
          <p>
            Alert: Your company Terms of Service are not defined. Add them now
            to inform users of your company's policies.
          </p>
        </Box>
      )}
      {isEdit ? (
        <>
          <Box className="editor-wrapper">
            <LexicalEditor
              handleDataChange={(html: any, children: any) => {
                setEditedTermsOfServices({
                  termsOfServiceJson: children,
                  termsOfServiceHtml: html,
                });
              }}
              defaultHtmlJson={
                isSuperTenant || showAFPTos
                  ? termsOfServices?.defaultTermsOfServices
                      ?.terms_of_service_html
                  : termsOfServices?.tenantTermsOfServices
                      ?.terms_of_service_html
              }
            />
          </Box>
          <Box className="terms-of-service-actions">
            <Button
              label="Cancel"
              className="cancel-button"
              onClick={handleCancel}
            />
            <Button
              label="Save"
              className="next-button-wrapper"
              onClick={() => {
                handleSave(
                  editedTermsOfServices?.termsOfServiceHtml,
                  editedTermsOfServices?.termsOfServiceJson
                );
              }}
            />
          </Box>
        </>
      ) : (
        <div className="terms-of-service-container">
          <article
            id="post-604"
            className="post-604 page type-page status-publish hentry"
          >
            <div
              dangerouslySetInnerHTML={{
                __html: isSuperTenant
                  ? termsOfServices?.defaultTermsOfServices
                      ?.terms_of_service_html
                  : termsOfServices?.tenantTermsOfServices
                      ?.terms_of_service_html,
              }}
            />
          </article>
        </div>
      )}

      {alert?.isOpen && (
        <AlertComponent
          message={alert?.message || ""}
          type={
            ["info", "success", "error", "warning"].includes(alert?.type || "")
              ? (alert?.type as "info" | "success" | "error" | "warning")
              : "info"
          }
          open={alert?.isOpen}
          onClose={() => setAlert({})}
        />
      )}
      {isDialogOpen &&
        termsDialog(
          isDialogOpen,
          handleClose,
          handleUseAFP,
          handleContinueCustom
        )}
    </div>
  );
};
export default UserTermsOfService;
